import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import './Jornada.css';

/**
 * O momento em que o cliente costuma ser esquecido.
 *
 * Este modal existe para uma coisa só: não deixar ninguém sair do turno com
 * chamado aberto na mão sem ter decidido o que fazer com ele. O servidor recusa
 * de qualquer forma (409 `JORNADA_COM_PENDENCIAS`); a tela existe para que a
 * recusa não seja a primeira notícia que o operador recebe.
 *
 * As duas formas de decidir convivem de propósito:
 *
 *   o seletor de cima  preenche TODOS os destinos de uma vez, que é o que
 *                      acontece na troca de turno normal — passa tudo para
 *                      quem entra, um clique;
 *   o seletor da linha manda em cada chamado, porque sempre há a exceção: o
 *                      guincho já a caminho que quem sai prefere entregar a
 *                      alguém específico.
 *
 * O de cima PREENCHE os de baixo em vez de substituí-los na hora de enviar.
 * Assim o operador vê na tela exatamente o que vai acontecer antes de
 * confirmar, e pode desfazer uma linha sem refazer as outras.
 *
 * ---------------------------------------------------------------------------
 * Por que vai para o `document.body` por portal, e não fica onde foi escrito
 *
 * Este componente é aberto pelo botão que mora no CABEÇALHO, e o cabeçalho tem
 * `backdrop-filter`. Qualquer ancestral com `transform`, `filter` ou
 * `backdrop-filter` cria um novo bloco de contenção, e a partir dali
 * `position: fixed` deixa de se ancorar na janela e passa a se ancorar NELE.
 *
 * O efeito medido: o fundo `inset: 0` cobria só a faixa de 52px do cabeçalho, o
 * conteúdo era centralizado dentro dessa faixa e **o topo da janela ficava
 * fora da tela** — título e metade do aviso inalcançáveis, inclusive rolando.
 * Nada no CSS deste arquivo estava errado; o problema era o lugar da árvore.
 *
 * O portal tira o componente de dentro do cabeçalho na hora de pintar, sem
 * mexer em onde ele é declarado. É a correção da causa: qualquer sobreposição
 * aberta de dentro do cabeçalho ou da barra lateral tem o mesmo problema.
 * ---------------------------------------------------------------------------
 */

const SEM_DONO = 'sem_dono';

export default function EncerrarJornadaModal({ operadorId = null, operadorNome = '', aoFechar, aoEncerrar }) {
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [pendentes, setPendentes] = useState([]);
  const [candidatos, setCandidatos] = useState([]);
  const [destinos, setDestinos] = useState({});
  const [observacao, setObservacao] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = operadorId ? { operador_id: operadorId } : {};
      const [resPendentes, resPlantao] = await Promise.all([
        api.get('/jornadas/pendencias', { params }),
        api.get('/jornadas/plantao'),
      ]);

      const lista = resPendentes.data?.data?.pendentes || [];
      setPendentes(lista);

      // Quem pode receber: de plantão agora e diferente de quem está saindo.
      // O servidor confere isto de novo; aqui é só para não oferecer o que
      // seria recusado.
      const alvo = resPendentes.data?.data?.operador_id;
      setCandidatos((resPlantao.data?.data || []).filter((p) => Number(p.operador_id) !== Number(alvo)));
    } catch {
      toast.error('Não foi possível carregar os atendimentos em aberto');
    } finally {
      setCarregando(false);
    }
  }, [operadorId]);

  useEffect(() => { carregar(); }, [carregar]);

  const aplicarEmLote = (valor) => {
    if (!valor) return;
    const novo = {};
    for (const p of pendentes) novo[p.id] = valor;
    setDestinos(novo);
  };

  const faltando = useMemo(
    () => pendentes.filter((p) => !destinos[p.id]),
    [pendentes, destinos],
  );

  const encerrar = async () => {
    if (faltando.length > 0) {
      toast.warning(`Faltam ${faltando.length} atendimento(s) sem destino definido`);
      return;
    }

    setEnviando(true);
    try {
      const corpo = {
        destinos: pendentes.map((p) => ({
          atendimento_id: p.id,
          // O servidor exige a palavra "sem_dono" e recusa nulo: campo ausente
          // e campo nulo chegam iguais depois de qualquer serialização
          // desatenta, e a diferença entre os dois é a diferença entre
          // "esqueci" e "decidi".
          para: destinos[p.id] === SEM_DONO ? SEM_DONO : Number(destinos[p.id]),
        })),
      };
      if (operadorId) corpo.operador_id = operadorId;
      if (observacao.trim()) corpo.observacao = observacao.trim();

      const res = await api.post('/jornadas/encerrar', corpo);
      toast.success(res.data?.message || 'Jornada encerrada');
      if (aoEncerrar) aoEncerrar();
      aoFechar();
    } catch (erro) {
      const dados = erro.response?.data?.error;
      if (dados?.code === 'JORNADA_COM_PENDENCIAS') {
        // Chegou chamado novo enquanto o modal estava aberto: recarrega em vez
        // de só reclamar, senão o operador não tem como sair da tela.
        toast.warning(dados.message);
        carregar();
      } else {
        toast.error(dados?.message || 'Não foi possível encerrar a jornada');
      }
    } finally {
      setEnviando(false);
    }
  };

  const semPendencia = !carregando && pendentes.length === 0;

  return createPortal((
    <div className="jornada-modal-fundo" role="dialog" aria-modal="true">
      <div className="jornada-modal">
        <div className="jornada-modal-cabecalho">
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2>Encerrar jornada</h2>
            <p className="jornada-modal-subtitulo">
              {operadorNome
                ? `Turno de ${operadorNome}`
                : 'Antes de sair, decida o destino de cada atendimento em aberto.'}
            </p>
          </div>
          <button type="button" className="btn-icon" onClick={aoFechar} title="Fechar">
            <span className="material-icons-round">close</span>
          </button>
        </div>

        <div className="jornada-modal-corpo">
          {carregando && <div className="jornada-vazio">Carregando atendimentos...</div>}

          {semPendencia && (
            <div className="jornada-vazio">
              Nenhum atendimento em aberto. Pode encerrar tranquilo.
            </div>
          )}

          {!carregando && pendentes.length > 0 && (
            <>
              <div className="jornada-aviso">
                <span className="material-icons-round" style={{ fontSize: 20 }}>info</span>
                <span>
                  {pendentes.length} atendimento(s) seguem em aberto. Cada um precisa de um
                  destino: outro operador de plantão, ou ficar explicitamente sem dono.
                </span>
              </div>

              <div className="jornada-lote">
                <div className="jornada-lote-campo">
                  <span className="jornada-rotulo">Passar todos para</span>
                  <select
                    className="jornada-select"
                    defaultValue=""
                    onChange={(e) => aplicarEmLote(e.target.value)}
                  >
                    <option value="">Escolher um a um</option>
                    {candidatos.map((c) => (
                      <option key={c.operador_id} value={c.operador_id}>
                        {c.operador_nome}
                        {c.situacao === 'em_pausa' ? ' (em pausa)' : ''}
                      </option>
                    ))}
                    <option value={SEM_DONO}>Deixar todos sem dono</option>
                  </select>
                </div>
              </div>

              {candidatos.length === 0 && (
                <div className="jornada-aviso">
                  <span className="material-icons-round" style={{ fontSize: 20 }}>person_off</span>
                  <span>
                    Ninguém mais está de plantão. Peça a quem entra para iniciar a jornada,
                    ou deixe os chamados sem dono sabendo que ninguém está olhando por eles.
                  </span>
                </div>
              )}

              {pendentes.map((p) => (
                <div className="jornada-pendencia" key={p.id}>
                  <div className="jornada-pendencia-dados">
                    <div className="jornada-pendencia-titulo">
                      {p.protocolo ? `#${p.protocolo}` : `Atendimento ${p.id}`}
                      {p.veiculo_placa ? ` — ${p.veiculo_placa}` : ''}
                    </div>
                    <div className="jornada-pendencia-linha">
                      {p.cliente_nome || 'Sem beneficiário'} · {p.status}
                    </div>
                  </div>
                  <div className="jornada-pendencia-destino">
                    <select
                      className={`jornada-select ${destinos[p.id] === SEM_DONO ? 'sem-dono' : ''}`}
                      value={destinos[p.id] || ''}
                      onChange={(e) => setDestinos((atual) => ({ ...atual, [p.id]: e.target.value }))}
                    >
                      <option value="">Escolha o destino...</option>
                      {candidatos.map((c) => (
                        <option key={c.operador_id} value={c.operador_id}>
                          Transferir para {c.operador_nome}
                        </option>
                      ))}
                      <option value={SEM_DONO}>Deixar sem dono</option>
                    </select>
                  </div>
                </div>
              ))}

              {/* Só aparece quando há o que explicar. Em jornada limpa, o
                  encerramento é um clique e nada mais. */}
              <div className="jornada-lote-campo" style={{ marginTop: 16 }}>
                <span className="jornada-rotulo">Observação (opcional)</span>
                <input
                  type="text"
                  className="jornada-select"
                  placeholder="Ex.: passei o plantão para o turno da manhã"
                  value={observacao}
                  maxLength={255}
                  onChange={(e) => setObservacao(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="jornada-modal-rodape">
          <button type="button" className="btn btn-secondary" onClick={aoFechar} disabled={enviando}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={encerrar}
            disabled={enviando || carregando || faltando.length > 0}
          >
            {enviando ? 'Encerrando...' : 'Encerrar jornada'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
